import { useCallback, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTheme } from '@mui/material/styles';

import { ResponsiveDialog } from '@buoyhealth/common.buoy-dialog';
import { CollapseAndFade } from '@buoyhealth/common.buoy-layout';
import {
  DEFAULT_TIPPING_CONFIG,
  HelpfulnessSurvey,
  ISurveyConfig,
} from '@buoyhealth/common.buoy-user-feedback';
import Form, { IForm } from './Form';
import { submitMailchimpForm } from './requests/mailchimp';
import { submitEmailForTranscript } from '../../requests/site_api/transcript';
import { trackFormExit, trackFormSubmitted, trackFormViewed } from './analytics';

interface IEmailTranscriptDialogProps {
  stripeApiKey?: string;
  interview: string;
  isVisible: boolean;
  surveyConfig: ISurveyConfig;
  shouldShowTipping?: boolean;
  tippingConfig?: typeof DEFAULT_TIPPING_CONFIG;
}

const Dialog = ({
  stripeApiKey,
  interview,
  isVisible: dialogIsVisible,
  surveyConfig,
  shouldShowTipping,
  tippingConfig,
}: IEmailTranscriptDialogProps) => {
  const theme = useTheme();

  const [shouldHideTitle, setShouldHideTitle] = useState(false);
  const [isDeclined, setIsDeclined] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [showError, setShowError] = useState(false);
  const { control, handleSubmit } = useForm<IForm>({
    defaultValues: {
      email: '',
      termsAgreed: false,
      newsletterAgreed: false,
    },
  });

  useEffect(() => {
    setIsVisible(dialogIsVisible);
    if (dialogIsVisible) {
      trackFormViewed(interview, 'popup');
    }
  }, [dialogIsVisible]);

  const onSubmit = useCallback(
    (data: IForm) => {
      if (data.termsAgreed) {
        try {
          setIsSubmitted(true);
          submitEmailForTranscript({ email: data.email, token: interview });
          if (data.newsletterAgreed) {
            submitMailchimpForm(data.email);
          }
          trackFormSubmitted(interview, data.newsletterAgreed, 'popup');
        } catch {
          setIsSubmitted(false);
          setShowError(true);
        }
      }
      setShowTermsError(!data.termsAgreed);
    },
    [isSubmitted, showTermsError],
  );

  const onDecline = useCallback(() => {
    setIsDeclined(true);
    trackFormExit(interview, 'popup');
  }, [isDeclined]);

  const onClose = () => {
    setIsVisible(false);
    trackFormExit(interview, 'popup');
  };

  const onSurveyCompletion = useCallback(
    (tippingIsVisible?: boolean) => {
      if (tippingIsVisible) {
        setShouldHideTitle(true);
      } else {
        setIsVisible(false);
      }
    },
    [isVisible],
  );

  return (
    <>
      <ResponsiveDialog
        actions={[
          {
            label: 'Submit',
            isCta: true,
            fullWidth: true,
            onClick: handleSubmit(onSubmit),
          },
          {
            label: 'No thanks',
            isTextBtn: true,
            onClick: () => onDecline(),
            sx: {
              textDecoration: 'underline',
              '&.MuiButtonBase-root': {
                color: theme.palette.gray[100],
                fontSize: '16px',
              },
            },
          },
        ]}
        actionsAsColumn={true}
        onClose={onClose}
        title={
          shouldHideTitle
            ? ''
            : 'Don’t lose your results! Get a copy of your visit sent to your email.'
        }
        drawerHeight="auto"
        drawerContentPadding="24px 24px 16px 24px"
        drawerFooterPadding="0 16px 24px 16px"
        open={isVisible}
        maxWidth={400}
      >
        <div>
          <CollapseAndFade
            baselineDelay={theme.transitions.duration.shorter}
            show={!isSubmitted && !isDeclined}
          >
            <Form
              control={control}
              showError={showTermsError}
              showGenericError={showError}
              formType="popup"
              interview={interview}
            />
          </CollapseAndFade>

          <CollapseAndFade show={isSubmitted || isDeclined}>
            <HelpfulnessSurvey
              canCollectUserEmail={isDeclined}
              displayDelay={0}
              skipCookieCheck
              surveyConfig={surveyConfig}
              customTitle={isDeclined ? 'No problem.' : 'Thank you 🙏'}
              subTitle={surveyConfig.question}
              renderLargeSubTitle={true}
              shouldHideModal={!isSubmitted && !isDeclined}
              shouldShowModal={isSubmitted || isDeclined}
              onSurveyCompletion={onSurveyCompletion}
              tippingEnabled={shouldShowTipping}
              tippingModuleData={{
                token: interview as any,
                config: tippingConfig as any,
                key: stripeApiKey as any,
              }}
              withBuoyTheme
            />
          </CollapseAndFade>
        </div>
      </ResponsiveDialog>
    </>
  );
};

export default Dialog;
